import React, { useState, useRef } from 'react';
import { X, AlertCircle, Upload, FileText, Trash2 } from 'lucide-react';
import { BUS_EMISSION_PARTNERS, BusEmissionPartner } from '../services/busCheckoutService';

interface BusCompletionModalProps {
  onClose: () => void;
  onConfirm: (data: BusCompletionData) => void;
  isLoading?: boolean;
  hasReturn?: boolean;
  passengers?: { id: number; name: string }[];
}

export interface BusCompletionData {
  vehicle_plate?: string;
  reserved_code_company?: string;
  emission_partner?: BusEmissionPartner;
  emitted_total_price?: number;
  seat_numbers?: string[];
  volta_vehicle_plate?: string;
  volta_reserved_code_company?: string;
  volta_emission_partner?: BusEmissionPartner;
  volta_seat_numbers?: string[];
  boardingPassFiles?: { file: File; passengerName: string; seat?: string }[];
}

export const BusCompletionModal: React.FC<BusCompletionModalProps> = ({
  onClose,
  onConfirm,
  isLoading = false,
  hasReturn = false,
  passengers = [],
}) => {
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [reservedCode, setReservedCode] = useState('');
  const [emissionPartner, setEmissionPartner] = useState<BusEmissionPartner | ''>('');
  const [emittedTotalPrice, setEmittedTotalPrice] = useState('');
  const [seatNumbers, setSeatNumbers] = useState('');
  const [voltaVehiclePlate, setVoltaVehiclePlate] = useState('');
  const [voltaReservedCode, setVoltaReservedCode] = useState('');
  const [voltaEmissionPartner, setVoltaEmissionPartner] = useState<BusEmissionPartner | ''>('');
  const [voltaSeatNumbers, setVoltaSeatNumbers] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  // Boarding pass files - pode ser um único para todos ou um por passageiro
  const [boardingPassMode, setBoardingPassMode] = useState<'single' | 'per_passenger'>('single');
  const [singleFile, setSingleFile] = useState<File | null>(null);
  const [perPassengerFiles, setPerPassengerFiles] = useState<Map<string, File>>(new Map());
  const singleFileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (passengerName: string, file: File | null) => {
    if (file) {
      setPerPassengerFiles(prev => {
        const next = new Map(prev);
        next.set(passengerName, file);
        return next;
      });
    } else {
      setPerPassengerFiles(prev => {
        const next = new Map(prev);
        next.delete(passengerName);
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors: string[] = [];
    
    if (!reservedCode.trim()) {
      validationErrors.push('Código de reserva da ida é obrigatório');
    }
    
    if (!seatNumbers.trim()) {
      validationErrors.push('Números de assento da ida são obrigatórios');
    }

    if (hasReturn) {
      if (!voltaReservedCode.trim()) {
        validationErrors.push('Código de reserva da volta é obrigatório');
      }
      
      if (!voltaSeatNumbers.trim()) {
        validationErrors.push('Números de assento da volta são obrigatórios');
      }
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const parsedSeats = seatNumbers.split(',').map(s => s.trim()).filter(s => s);

    const data: BusCompletionData = {
      reserved_code_company: reservedCode.trim(),
      seat_numbers: parsedSeats,
    };

    if (vehiclePlate.trim()) {
      data.vehicle_plate = vehiclePlate.trim();
    }

    if (emissionPartner) {
      data.emission_partner = emissionPartner;
    }

    if (emittedTotalPrice.trim()) {
      const parsed = parseFloat(emittedTotalPrice.replace(',', '.'));
      if (!isNaN(parsed) && parsed > 0) {
        data.emitted_total_price = parsed;
      }
    }

    if (hasReturn) {
      data.volta_reserved_code_company = voltaReservedCode.trim();
      data.volta_seat_numbers = voltaSeatNumbers.split(',').map(s => s.trim()).filter(s => s);
      
      if (voltaVehiclePlate.trim()) {
        data.volta_vehicle_plate = voltaVehiclePlate.trim();
      }

      if (voltaEmissionPartner) {
        data.volta_emission_partner = voltaEmissionPartner;
      }
    }

    // Montar os arquivos de boarding pass
    const boardingPassFiles: { file: File; passengerName: string; seat?: string }[] = [];

    if (boardingPassMode === 'single' && singleFile) {
      // Um único PDF para todos os passageiros - associar ao primeiro
      const firstPassenger = passengers.length > 0 ? passengers[0].name : 'Passageiro';
      boardingPassFiles.push({
        file: singleFile,
        passengerName: firstPassenger,
        seat: parsedSeats[0],
      });
    } else if (boardingPassMode === 'per_passenger') {
      passengers.forEach((p, index) => {
        const file = perPassengerFiles.get(p.name);
        if (file) {
          boardingPassFiles.push({
            file,
            passengerName: p.name,
            seat: parsedSeats[index],
          });
        }
      });
    }

    if (boardingPassFiles.length > 0) {
      data.boardingPassFiles = boardingPassFiles;
    }

    onConfirm(data);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(2px)'
    }}>
      <div className="card" style={{
        width: '90%',
        maxWidth: '580px',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        padding: '32px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e5e7eb'
        }}>
          <h2 style={{ 
            margin: 0,
            fontSize: '20px',
            fontWeight: 700,
            color: '#111827'
          }}>
            Completar Emissão {hasReturn && '(Ida e Volta)'}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              opacity: isLoading ? 0.5 : 1,
              borderRadius: '6px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={24} color="#6b7280" />
          </button>
        </div>

        {errors.length > 0 && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '2px solid #fecaca',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '20px',
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start'
          }}>
            <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              {errors.map((error, index) => (
                <div key={index} style={{ 
                  color: '#dc2626', 
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: index < errors.length - 1 ? '4px' : '0'
                }}>
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 700, 
            color: '#1e90ff', 
            marginBottom: '16px',
            marginTop: '0'
          }}>
            🚌 Dados da Ida
          </h3>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '14px',
              color: '#333'
            }}>
              Placa do Veículo <span style={{ color: '#999', fontWeight: 400, fontSize: '13px' }}>(opcional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={vehiclePlate}
              onChange={(e) => setVehiclePlate(e.target.value)}
              placeholder="Ex: ABC-1234"
              disabled={isLoading}
              style={{
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '14px',
              color: '#333'
            }}>
              Código de Reserva <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={reservedCode}
              onChange={(e) => setReservedCode(e.target.value)}
              placeholder="Ex: RES-123456"
              disabled={isLoading}
              required
              style={{
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                transition: 'all 0.2s ease'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '14px',
              color: '#333'
            }}>
              Parceiro de Emissão <span style={{ color: '#999', fontWeight: 400, fontSize: '13px' }}>(opcional)</span>
            </label>
            <select
              className="form-select"
              value={emissionPartner}
              onChange={(e) => setEmissionPartner(e.target.value as BusEmissionPartner | '')}
              disabled={isLoading}
              style={{
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
              }}
            >
              <option value="">— Selecione o parceiro —</option>
              {BUS_EMISSION_PARTNERS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '14px',
              color: '#333'
            }}>
              Preço Total Emitido (R$) <span style={{ color: '#999', fontWeight: 400, fontSize: '13px' }}>(opcional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={emittedTotalPrice}
              onChange={(e) => setEmittedTotalPrice(e.target.value)}
              placeholder="Ex: 85.50"
              disabled={isLoading}
              style={{
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                transition: 'all 0.2s ease'
              }}
            />
            <small style={{ 
              color: '#6b7280', 
              fontSize: '13px', 
              marginTop: '6px', 
              display: 'block',
              fontStyle: 'italic'
            }}>
              💡 Valor cobrado pelo parceiro — para controle financeiro interno
            </small>
          </div>

          <div style={{ marginBottom: '28px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: 600,
              fontSize: '14px',
              color: '#333'
            }}>
              Números de Assento <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={seatNumbers}
              onChange={(e) => setSeatNumbers(e.target.value)}
              placeholder="Ex: 42, 43, 44"
              disabled={isLoading}
              required
              style={{
                fontSize: '14px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                transition: 'all 0.2s ease'
              }}
            />
            <small style={{ 
              color: '#6b7280', 
              fontSize: '13px', 
              marginTop: '6px', 
              display: 'block',
              fontStyle: 'italic'
            }}>
              💡 Separe os números por vírgula
            </small>
          </div>

          {hasReturn && (
            <>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: 700, 
                color: '#1e90ff', 
                marginBottom: '16px',
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '2px dashed #e5e7eb'
              }}>
                🔄 Dados da Volta
              </h3>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Placa do Veículo <span style={{ color: '#999', fontWeight: 400, fontSize: '13px' }}>(opcional)</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={voltaVehiclePlate}
                  onChange={(e) => setVoltaVehiclePlate(e.target.value)}
                  placeholder="Ex: XYZ-5678"
                  disabled={isLoading}
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    transition: 'all 0.2s ease'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Código de Reserva <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={voltaReservedCode}
                  onChange={(e) => setVoltaReservedCode(e.target.value)}
                  placeholder="Ex: RES-789012"
                  disabled={isLoading}
                  required
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    transition: 'all 0.2s ease'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Parceiro de Emissão <span style={{ color: '#999', fontWeight: 400, fontSize: '13px' }}>(opcional)</span>
                </label>
                <select
                  className="form-select"
                  value={voltaEmissionPartner}
                  onChange={(e) => setVoltaEmissionPartner(e.target.value as BusEmissionPartner | '')}
                  disabled={isLoading}
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                  }}
                >
                  <option value="">— Selecione o parceiro —</option>
                  {BUS_EMISSION_PARTNERS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '28px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#333'
                }}>
                  Números de Assento <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={voltaSeatNumbers}
                  onChange={(e) => setVoltaSeatNumbers(e.target.value)}
                  placeholder="Ex: 15, 16, 17"
                  disabled={isLoading}
                  required
                  style={{
                    fontSize: '14px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    transition: 'all 0.2s ease'
                  }}
                />
                <small style={{ 
                  color: '#6b7280', 
                  fontSize: '13px', 
                  marginTop: '6px', 
                  display: 'block',
                  fontStyle: 'italic'
                }}>
                  💡 Separe os números por vírgula
                </small>
              </div>
            </>
          )}

          {/* Seção de Upload de Cartão de Embarque */}
          <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '2px dashed #e5e7eb'
          }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#059669', 
              marginBottom: '12px',
              marginTop: '0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <FileText size={18} />
              Cartão de Embarque (PDF)
            </h3>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '13px', 
              marginBottom: '16px',
              marginTop: '0'
            }}>
              Anexe o(s) bilhete(s) de embarque em PDF para enviar ao cliente via WhatsApp. <span style={{ fontWeight: 500 }}>(opcional)</span>
            </p>

            {/* Toggle: único PDF ou um por passageiro */}
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              marginBottom: '16px' 
            }}>
              <button
                type="button"
                onClick={() => setBoardingPassMode('single')}
                disabled={isLoading}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: boardingPassMode === 'single' ? '2px solid #059669' : '2px solid #e5e7eb',
                  backgroundColor: boardingPassMode === 'single' ? '#ecfdf5' : '#fff',
                  color: boardingPassMode === 'single' ? '#059669' : '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📄 PDF Único
              </button>
              <button
                type="button"
                onClick={() => setBoardingPassMode('per_passenger')}
                disabled={isLoading || passengers.length === 0}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  border: boardingPassMode === 'per_passenger' ? '2px solid #059669' : '2px solid #e5e7eb',
                  backgroundColor: boardingPassMode === 'per_passenger' ? '#ecfdf5' : '#fff',
                  color: boardingPassMode === 'per_passenger' ? '#059669' : '#6b7280',
                  cursor: passengers.length === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                👥 Um por Passageiro
              </button>
            </div>

            {boardingPassMode === 'single' && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    ref={singleFileRef}
                    type="file"
                    accept="application/pdf"
                    disabled={isLoading}
                    onChange={(e) => setSingleFile(e.target.files?.[0] || null)}
                    style={{ fontSize: '13px', flex: 1 }}
                  />
                  {singleFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setSingleFile(null);
                        if (singleFileRef.current) singleFileRef.current.value = '';
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Trash2 size={16} color="#dc2626" />
                    </button>
                  )}
                </div>
                {singleFile && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <FileText size={14} />
                    {singleFile.name}
                  </div>
                )}
              </div>
            )}

            {boardingPassMode === 'per_passenger' && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {passengers.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: perPassengerFiles.has(p.name) ? '1px solid #059669' : '1px solid #e2e8f0',
                    }}
                  >
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#333',
                      marginBottom: '8px'
                    }}>
                      {p.name}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="file"
                        accept="application/pdf"
                        disabled={isLoading}
                        onChange={(e) => handleFileChange(p.name, e.target.files?.[0] || null)}
                        style={{ fontSize: '12px', flex: 1 }}
                      />
                      {perPassengerFiles.has(p.name) && (
                        <button
                          type="button"
                          onClick={() => handleFileChange(p.name, null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Trash2 size={14} color="#dc2626" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {passengers.length === 0 && (
                  <p style={{ color: '#9ca3af', fontSize: '13px', fontStyle: 'italic', margin: 0 }}>
                    Nenhum passageiro encontrado no checkout.
                  </p>
                )}
              </div>
            )}
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            paddingTop: '24px',
            marginTop: '24px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 600
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isLoading ? 'Salvando...' : (
                <>
                  <Upload size={16} />
                  Confirmar e Enviar
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
